import React, { useEffect, useState } from "react";
import S3Uploader from "@/components/S3Uploader";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Loader2, X, Plus, Trash2, ChevronUp, ChevronDown, Eye, PenLine, GripVertical } from "lucide-react";

const FIELD_TYPES = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Paragraph" },
  { value: "number", label: "Number" },
  { value: "dropdown", label: "Dropdown" },
  { value: "multiselect", label: "Multi-select" },
  { value: "checkbox", label: "Checkbox" },
  { value: "radio", label: "Radio buttons" },
  { value: "toggle", label: "Yes / No toggle" },
  { value: "date", label: "Date" },
  { value: "signature", label: "Signature" },
  { value: "file", label: "File upload" },
  { value: "trailer", label: "Trailer (Plant & Transport)" },
  { value: "forklift", label: "Forklift (Plant & Transport)" },
  { value: "time", label: "Time" },
  { value: "product_repeater", label: "Product list (repeater)" },
  { value: "section", label: "Section header" },
  { value: "notice", label: "Instruction / notice" },
];
const PER_PRODUCT_OK = new Set(["text", "number", "dropdown", "radio", "toggle", "checkbox", "date"]);
const ACTIONS = [{ value: "", label: "No action" }, { value: "mark_returned", label: "Mark stock product assignments returned" }, { value: "marketing_consent", label: "Customer marketing consent for installation media" }];
const TYPE_LABEL = Object.fromEntries(FIELD_TYPES.map((t) => [t.value, t.label]));
const HAS_OPTIONS = new Set(["dropdown", "multiselect", "radio"]);
const HAS_PLACEHOLDER = new Set(["text", "textarea", "number"]);
const OPS = [
  { value: "eq", label: "is equal to" },
  { value: "neq", label: "is not equal to" },
  { value: "gt", label: "is greater than" },
  { value: "lt", label: "is less than" },
  { value: "contains", label: "contains" },
  { value: "not_empty", label: "is answered" },
  { value: "empty", label: "is empty" },
];
const NO_VALUE = new Set(["empty", "not_empty"]);

const selCls =
  "w-full appearance-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 pr-8 text-sm text-slate-900 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 focus:outline-none";

const uid = () => Math.random().toString(36).slice(2, 10);
const newField = (type = "text") => ({
  id: uid(), type, label: "", placeholder: "", help: "", required: false,
  options: HAS_OPTIONS.has(type) ? ["Option 1", "Option 2"] : [],
  visibility: { enabled: false, match: "all", rules: [] },
  per_product: false, multiple: false, accept: "", max_files: 0, allow_na: false, action: "", default: "",
});

// Evaluate whether a field is visible given the current answers (shared with preview)
export function evalVisibility(field, answers) {
  const v = field.visibility;
  if (!v || !v.enabled || !(v.rules || []).length) return true;
  const results = (v.rules || []).map((r) => {
    const a = answers[r.field];
    const val = r.value;
    switch (r.op) {
      case "eq": return String(a ?? "") === String(val);
      case "neq": return String(a ?? "") !== String(val);
      case "gt": return Number(a) > Number(val);
      case "lt": return Number(a) < Number(val);
      case "contains":
        return Array.isArray(a)
          ? a.map(String).includes(String(val))
          : String(a ?? "").toLowerCase().includes(String(val).toLowerCase());
      case "not_empty":
        if (typeof a === "boolean") return a === true;
        return Array.isArray(a) ? a.length > 0 : String(a ?? "").trim() !== "";
      case "empty":
        if (typeof a === "boolean") return a === false;
        return Array.isArray(a) ? a.length === 0 : String(a ?? "").trim() === "";
      default: return true;
    }
  });
  return v.match === "any" ? results.some(Boolean) : results.every(Boolean);
}

const FORM_USAGE = ["Off Site / Desktop", "On Site / App", "Pre Site"];
const FORM_CATEGORIES = ["Vehicle Checks", "Service & Installation", "Customer Pre-Visit", "Health & Safety", "Shift & Job", "Finance", "Other"];
const Chevron = () => <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />;
const Label = ({ children }) => <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{children}</label>;

export default function FormBuilder({ form, canManage, onClose, onSaved }) {
  const isEdit = !!form?.id;
  const [name, setName] = useState(form?.name || "");
  const [description, setDescription] = useState(form?.description || "");
  const [usage, setUsage] = useState(form?.usage || "On Site / App");
  const [category, setCategory] = useState(form?.category || "");
  const [isTraining, setIsTraining] = useState(!!form?.is_training);
  const [trainingRoles, setTrainingRoles] = useState(form?.training_roles || []);
  const [roleOpts, setRoleOpts] = useState([]);
  useEffect(() => { if (isTraining && !roleOpts.length) { api.get("/forms/meta/roles").then((r) => setRoleOpts(r.data.roles)).catch(() => {}); } }, [isTraining, roleOpts.length]);
  const [fields, setFields] = useState(() =>
    (form?.fields || []).map((f) => ({
      ...newField(f.type), ...f,
      visibility: { enabled: false, match: "all", rules: [], ...(f.visibility || {}) },
    }))
  );
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);

  const patch = (i, updater) => setFields((s) => s.map((f, idx) => (idx === i ? { ...f, ...updater(f) } : f)));
  const move = (i, dir) => setFields((s) => {
    const j = i + dir;
    if (j < 0 || j >= s.length) return s;
    const copy = [...s];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });
  const remove = (i) => setFields((s) => {
    const removedId = s[i]?.id;
    return s
      .filter((_, idx) => idx !== i)
      .map((f) => (f.visibility?.rules?.some((r) => r.field === removedId)
        ? { ...f, visibility: { ...f.visibility, rules: f.visibility.rules.filter((r) => r.field !== removedId) } }
        : f));
  });
  const add = (type) => setFields((s) => [...s, newField(type)]);

  const save = async () => {
    if (!name.trim()) { toast.error("Form name is required"); return; }
    if (isTraining && !trainingRoles.length) { toast.error("Pick at least one role for this training form"); return; }
    setBusy(true);
    const payload = { name: name.trim(), description, usage, category, fields, is_training: isTraining, training_roles: trainingRoles };
    try {
      if (isEdit) { await api.put(`/forms/${form.id}`, payload); toast.success("Form saved"); }
      else { await api.post(`/forms`, payload); toast.success("Form created"); }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save form"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex justify-end" onClick={onClose} data-testid="form-builder">
      <div className="bg-white h-full w-full sm:w-1/2 sm:max-w-none shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <h3 className="text-xl font-bold text-slate-900">{isEdit ? "Edit Form" : "New Form"}</h3>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setPreview((v) => !v)} data-testid="form-preview-toggle">
              {preview ? <PenLine className="w-4 h-4" /> : <Eye className="w-4 h-4" />}{preview ? "Edit" : "Preview"}
            </Button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 gap-4">
            <div><Label>Form Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Installation sign-off" className="mt-1" data-testid="form-name" disabled={preview} /></div>
            <div><Label>Description</Label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Optional description shown at the top of the form"
                className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100" data-testid="form-description" disabled={preview} /></div>
            <div><Label>Where is this form used?</Label>
              <div className="relative mt-1">
                <select value={usage} onChange={(e) => setUsage(e.target.value)} disabled={preview}
                  className="w-full appearance-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 pr-8 text-sm text-slate-900 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 focus:outline-none disabled:opacity-60" data-testid="form-usage">
                  {FORM_USAGE.map((u) => <option key={u} value={u}>{u}</option>)}
                </select><Chevron />
              </div></div>
            <div><Label>Category</Label>
              <div className="relative mt-1">
                <select value={category} onChange={(e) => setCategory(e.target.value)} disabled={preview}
                  className="w-full appearance-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 pr-8 text-sm text-slate-900 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 focus:outline-none disabled:opacity-60" data-testid="form-category">
                  <option value="">Uncategorised</option>
                  {FORM_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select><Chevron />
              </div></div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="form-training-box">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
                <input type="checkbox" checked={isTraining} onChange={(e) => setIsTraining(e.target.checked)} disabled={preview} data-testid="form-is-training" />
                Training form
              </label>
              <p className="mt-1 text-xs text-slate-500">Shown under My documents on staff profiles (desktop and app) and in HR Documents. Each person signs it once, then it's locked.</p>
              {isTraining && (
                <div className="mt-2 flex flex-wrap gap-2" data-testid="form-training-roles">
                  {roleOpts.map((r) => (
                    <label key={r.key} className={`cursor-pointer rounded-full border px-2.5 py-1 text-xs ${trainingRoles.includes(r.key) ? "border-indigo-400 bg-indigo-50 text-indigo-700" : "border-slate-200 bg-white text-slate-600"}`}>
                      <input type="checkbox" className="sr-only" checked={trainingRoles.includes(r.key)} disabled={preview} data-testid={`form-training-role-${r.key}`}
                        onChange={(e) => setTrainingRoles((s) => (e.target.checked ? [...s, r.key] : s.filter((k) => k !== r.key)))} />{r.name}
                    </label>))}
                </div>)}
            </div>
          </div>

          {preview ? (
            <FormPreview name={name} description={description} fields={fields} />
          ) : (
            <>
              <div className="space-y-3" data-testid="form-fields">
                {fields.length === 0 && (
                  <div className="text-center text-sm text-slate-400 py-6 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                    No fields yet. Add your first field below.
                  </div>
                )}
                {fields.map((f, i) => (
                  <FieldEditor key={f.id} field={f} index={i} total={fields.length}
                    others={fields.filter((_, idx) => idx !== i)}
                    onChange={(u) => patch(i, () => u)} onMove={(d) => move(i, d)} onRemove={() => remove(i)} />
                ))}
              </div>

              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Add a field</div>
                <div className="flex flex-wrap gap-2">
                  {FIELD_TYPES.map((t) => (
                    <button key={t.value} onClick={() => add(t.value)} data-testid={`form-add-field-${t.value}`}
                      className="inline-flex items-center gap-1 text-xs font-medium rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 transition-colors">
                      <Plus className="w-3.5 h-3.5" /> {t.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
          {isEdit && (
            <div className="rounded-xl border border-slate-200 p-4" data-testid="form-attachments">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Attachments</div>
              <p className="text-[11px] text-slate-400 mb-3">Reference files for this form template (guidance PDFs, example photos).</p>
              <S3Uploader recordType="forms" recordId={form.id} category="Form Reference" canManage={canManage} serverSide />
            </div>
          )}
        </div>

        {canManage && (
          <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={save} data-testid="form-save">
              {busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save Form
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function FieldEditor({ field, index, total, others, onChange, onMove, onRemove }) {
  const isSection = field.type === "section";
  const set = (k, v) => onChange({ ...field, [k]: v });
  const setVis = (u) => onChange({ ...field, visibility: { ...field.visibility, ...u } });
  const setRule = (ri, k, v) => setVis({ rules: field.visibility.rules.map((r, idx) => (idx === ri ? { ...r, [k]: v } : r)) });
  const addRule = () => setVis({ rules: [...field.visibility.rules, { id: uid(), field: others[0]?.id || "", op: "eq", value: "" }] });
  const removeRule = (ri) => setVis({ rules: field.visibility.rules.filter((_, idx) => idx !== ri) });

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`form-field-${index}`}>
      <div className="flex items-center gap-2 mb-3">
        <GripVertical className="w-4 h-4 text-slate-300 shrink-0" />
        <div className="relative flex-1 min-w-0">
          <select value={field.type} onChange={(e) => onChange({ ...field, type: e.target.value, options: HAS_OPTIONS.has(e.target.value) && !(field.options || []).length ? ["Option 1", "Option 2"] : field.options })}
            className={selCls} data-testid={`form-field-type-${index}`}>
            {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <Chevron />
        </div>
        <button onClick={() => onMove(-1)} disabled={index === 0} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move up"><ChevronUp className="w-4 h-4" /></button>
        <button onClick={() => onMove(1)} disabled={index === total - 1} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move down"><ChevronDown className="w-4 h-4" /></button>
        <button onClick={onRemove} className="text-slate-400 hover:text-rose-600" title="Delete field" data-testid={`form-field-remove-${index}`}><Trash2 className="w-4 h-4" /></button>
      </div>

      <div className="space-y-3">
        <div>
          <Label>{isSection ? "Section title" : "Question / label"}</Label>
          <Input value={field.label} onChange={(e) => set("label", e.target.value)} placeholder={isSection ? "Section heading" : "What should we ask?"} className="mt-1" data-testid={`form-field-label-${index}`} />
        </div>

        {HAS_PLACEHOLDER.has(field.type) && (
          <div><Label>Placeholder</Label>
            <Input value={field.placeholder} onChange={(e) => set("placeholder", e.target.value)} className="mt-1" data-testid={`form-field-placeholder-${index}`} /></div>
        )}

        {HAS_OPTIONS.has(field.type) && (
          <div><Label>Options (one per line)</Label>
            <textarea value={(field.options || []).join("\n")} onChange={(e) => set("options", e.target.value.split("\n"))}
              rows={3} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-100" data-testid={`form-field-options-${index}`}
              onBlur={(e) => set("options", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} /></div>
        )}

        {!isSection && (
          <div><Label>Help text (optional)</Label>
            <Input value={field.help} onChange={(e) => set("help", e.target.value)} className="mt-1" data-testid={`form-field-help-${index}`} /></div>
        )}

        {!isSection && (
          <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
            <span className="text-sm text-slate-700">Required</span>
            <Switch checked={field.required} onCheckedChange={(v) => set("required", v)} data-testid={`form-field-required-${index}`} />
          </div>
        )}
        <FieldExtras field={field} index={index} set={set} />

        {/* Conditional logic */}
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">Conditional logic</span>
            <Switch checked={field.visibility.enabled} onCheckedChange={(v) => setVis({ enabled: v, rules: v && !field.visibility.rules.length ? [{ id: uid(), field: others[0]?.id || "", op: "eq", value: "" }] : field.visibility.rules })} data-testid={`form-field-logic-${index}`} />
          </div>
          {field.visibility.enabled && (
            <div className="mt-3 space-y-2">
              {others.length === 0 ? (
                <div className="text-xs text-amber-700 bg-amber-50 rounded-md px-2 py-1.5">Add another field first — conditions compare against other fields.</div>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    Show this field when
                    <div className="relative">
                      <select value={field.visibility.match} onChange={(e) => setVis({ match: e.target.value })} className={`${selCls} py-1 w-24`} data-testid={`form-field-match-${index}`}>
                        <option value="all">ALL</option>
                        <option value="any">ANY</option>
                      </select>
                      <Chevron />
                    </div>
                    of these match:
                  </div>
                  {field.visibility.rules.map((r, ri) => (
                    <div key={r.id || ri} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-1.5 items-center" data-testid={`form-field-rule-${index}-${ri}`}>
                      <div className="relative">
                        <select value={r.field} onChange={(e) => setRule(ri, "field", e.target.value)} className={`${selCls} text-xs`} data-testid={`form-field-rule-field-${index}-${ri}`}>
                          <option value="">Select field…</option>
                          {others.map((o) => <option key={o.id} value={o.id}>{o.label || `(${TYPE_LABEL[o.type]})`}</option>)}
                        </select>
                        <Chevron />
                      </div>
                      <div className="relative">
                        <select value={r.op} onChange={(e) => setRule(ri, "op", e.target.value)} className={`${selCls} text-xs`} data-testid={`form-field-rule-op-${index}-${ri}`}>
                          {OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <Chevron />
                      </div>
                      {NO_VALUE.has(r.op)
                        ? <div className="text-xs text-slate-400 px-2">—</div>
                        : (others.find((o) => o.id === r.field)?.type === "toggle")
                          ? <div className="relative">
                              <select value={r.value} onChange={(e) => setRule(ri, "value", e.target.value)} className={`${selCls} text-xs`} data-testid={`form-field-rule-value-${index}-${ri}`}>
                                <option value="Yes">Yes</option>
                                <option value="No">No</option>
                              </select><Chevron />
                            </div>
                          : <Input value={r.value} onChange={(e) => setRule(ri, "value", e.target.value)} placeholder="value" className="text-xs h-9" data-testid={`form-field-rule-value-${index}-${ri}`} />}
                      <button onClick={() => removeRule(ri)} className="text-slate-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={addRule} data-testid={`form-field-add-rule-${index}`}><Plus className="w-3 h-3" /> Add condition</Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ToggleRow({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2">
      <div><div className="text-sm text-slate-700">{label}</div>{hint && <div className="text-[11px] text-slate-400">{hint}</div>}</div>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

function FieldExtras({ field, index, set }) {
  const isFile = field.type === "file";
  const canPerProduct = PER_PRODUCT_OK.has(field.type);
  const canAction = field.type === "product_repeater" || (canPerProduct && field.per_product) || field.type === "toggle";
  return (
    <>
      {canPerProduct && <ToggleRow label="Answer per product" hint="Repeat this question once for each product on the job" checked={field.per_product} onChange={(v) => set("per_product", v)} testid={`form-field-per-product-${index}`} />}
      {isFile && <ToggleRow label="Allow multiple files" checked={field.multiple} onChange={(v) => set("multiple", v)} testid={`form-field-multiple-${index}`} />}
      {isFile && (
        <div><Label>Allowed files</Label>
          <div className="relative mt-1">
            <select value={field.accept || ""} onChange={(e) => set("accept", e.target.value)} className={selCls} data-testid={`form-field-accept-${index}`}>
              <option value="">Any file</option><option value="media">Photos & videos (camera + gallery)</option>
              <option value="image">Photos only (camera + gallery)</option><option value="video">Videos only (record + gallery)</option>
            </select><Chevron />
          </div>
        </div>
      )}
      {isFile && field.multiple && (
        <div><Label>Maximum files</Label>
          <Input type="number" min={1} max={100} className="mt-1" value={field.max_files || ""} placeholder="30" onChange={(e) => set("max_files", Math.min(100, Math.max(0, parseInt(e.target.value || "0", 10))))} data-testid={`form-field-max-files-${index}`} />
        </div>
      )}
      {field.type === "toggle" && <ToggleRow label="Offer Not Applicable" hint="Adds an N/A choice next to Yes / No" checked={field.allow_na} onChange={(v) => set("allow_na", v)} testid={`form-field-allow-na-${index}`} />}
      {(field.type === "date" || field.type === "text" || field.type === "time") && (
        <div><Label>Pre-fill</Label>
          <select className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm" value={field.default || ""} onChange={(e) => set("default", e.target.value)} data-testid={`form-field-default-${index}`}>
            <option value="">Nothing</option>
            {field.type === "date" ? <option value="today">Today's date (editable)</option> : field.type === "time" ? <option value="now">Current time (editable)</option> : <option value="current_user">Logged-in user's name</option>}
          </select></div>
      )}
      {canAction && (
        <div><Label>Action on submit</Label>
          <div className="relative mt-1">
            <select value={field.action || ""} onChange={(e) => set("action", e.target.value)} className={selCls} data-testid={`form-field-action-${index}`}>
              {ACTIONS.filter((a) => (field.type === "toggle" && !field.per_product ? a.value !== "mark_returned" : a.value !== "marketing_consent")).map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select><Chevron />
          </div>
          {field.action === "marketing_consent" && <p className="text-[11px] text-slate-400 mt-1">When answered Yes, this job's installation photos and videos are marked Approved for marketing (now and any added later). Media someone has already Declined is left alone.</p>}
          {field.action === "mark_returned" && <p className="text-[11px] text-slate-400 mt-1">{field.per_product ? "Products answered Yes (or with a quantity) are marked returned and any deducted stock is put back." : "Each listed product and quantity is booked back into the warehouse."}</p>}
        </div>
      )}
    </>
  );
}

function FormPreview({ name, description, fields }) {
  const [answers, setAnswers] = useState({});
  const set = (id, v) => setAnswers((s) => ({ ...s, [id]: v }));
  const toggleMulti = (id, opt) => setAnswers((s) => {
    const cur = Array.isArray(s[id]) ? s[id] : [];
    return { ...s, [id]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
  });

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-5" data-testid="form-preview">
      <div className="mb-4">
        <h2 className="text-lg font-bold text-slate-900">{name || "Untitled form"}</h2>
        {description && <p className="text-sm text-slate-500 mt-1">{description}</p>}
      </div>
      <div className="space-y-4">
        {fields.filter((f) => evalVisibility(f, answers)).map((f) => {
          if (f.type === "section") return <h3 key={f.id} className="text-sm font-bold text-slate-800 uppercase tracking-wide border-b border-slate-200 pb-1 pt-2">{f.label || "Section"}</h3>;
          if (f.type === "notice") return <div key={f.id} className="rounded-xl border-2 border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900">{f.label || "Instruction"}</div>;
          const lbl = (
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {f.label || "Untitled"} {f.required && <span className="text-rose-500">*</span>}
            </label>
          );
          const help = f.help && <p className="text-xs text-slate-400 mt-1">{f.help}</p>;
          return (
            <div key={f.id} data-testid={`preview-field-${f.id}`}>
              {f.type !== "checkbox" && lbl}
              {f.type === "text" && <Input value={answers[f.id] || ""} onChange={(e) => set(f.id, e.target.value)} placeholder={f.placeholder} />}
              {f.type === "number" && <Input type="number" value={answers[f.id] || ""} onChange={(e) => set(f.id, e.target.value)} placeholder={f.placeholder} />}
              {f.type === "date" && <Input type="date" value={answers[f.id] || ""} onChange={(e) => set(f.id, e.target.value)} />}
              {f.type === "textarea" && <textarea value={answers[f.id] || ""} onChange={(e) => set(f.id, e.target.value)} placeholder={f.placeholder} rows={3} className="w-full rounded-lg border border-slate-200 bg-white p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100" />}
              {f.type === "dropdown" && (
                <div className="relative">
                  <select value={answers[f.id] || ""} onChange={(e) => set(f.id, e.target.value)} className={selCls}>
                    <option value="">Please select</option>
                    {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select><Chevron />
                </div>
              )}
              {f.type === "radio" && (
                <div className="space-y-1.5">
                  {(f.options || []).map((o) => (
                    <label key={o} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <input type="radio" name={f.id} checked={answers[f.id] === o} onChange={() => set(f.id, o)} className="accent-indigo-600" /> {o}
                    </label>
                  ))}
                </div>
              )}
              {f.type === "toggle" && (
                <div className="flex items-center gap-3">
                  <Switch checked={answers[f.id] === "Yes"} onCheckedChange={(v) => set(f.id, v ? "Yes" : "No")} data-testid={`preview-toggle-${f.id}`} />
                  <span className={`text-sm font-medium ${answers[f.id] === "Yes" ? "text-emerald-600" : answers[f.id] === "No" ? "text-rose-600" : "text-slate-400"}`}>
                    {answers[f.id] || "Not set"}
                  </span>
                </div>
              )}
              {f.type === "multiselect" && (
                <div className="space-y-1.5">
                  {(f.options || []).map((o) => (
                    <label key={o} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <input type="checkbox" checked={(answers[f.id] || []).includes(o)} onChange={() => toggleMulti(f.id, o)} className="accent-indigo-600" /> {o}
                    </label>
                  ))}
                </div>
              )}
              {f.type === "checkbox" && (
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={!!answers[f.id]} onChange={(e) => set(f.id, e.target.checked)} className="accent-indigo-600" />
                  {f.label || "Checkbox"} {f.required && <span className="text-rose-500">*</span>}
                </label>
              )}
              {f.type === "signature" && <div className="h-20 rounded-lg border-2 border-dashed border-slate-300 bg-white flex items-center justify-center text-sm text-slate-400 italic">Signature area</div>}
              {(f.per_product || f.type === "product_repeater") && <div className="text-xs text-slate-500 bg-white rounded-lg border border-dashed border-slate-300 px-3 py-2">{f.per_product ? "Repeated for each product on the job" : "Engineer adds products and quantities"}</div>}
              {f.type === "file" && <input type="file" multiple={!!f.multiple} accept={f.accept === "media" ? "image/*,video/*" : undefined} className="block w-full text-sm text-slate-500 file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:px-3 file:py-1.5 file:text-indigo-700" />}
              {f.type !== "checkbox" && help}
            </div>
          );
        })}
        {fields.length === 0 && <div className="text-center text-sm text-slate-400 py-6">Nothing to preview yet.</div>}
      </div>
    </div>
  );
}
